import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  Bot,
  ShieldCheck,
  RefreshCw,
  Languages
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Globe, ChevronDown, Check } from "lucide-react";
import { 
  getReferralCode, 
  getReferralData, 
  clearReferralCode 
} from "@/utils/referralTracking";
import { safeLocalStorage } from "@/utils/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Professional Floating Language Toggle for Auth Pages
const FloatingLanguageToggle = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  return (
    <div className="fixed top-4 right-4 z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="auth-language-toggle"
        className="flex items-center gap-2 px-3 py-2 rounded-lg glass border border-white/20 hover:border-white/40 transition-all"
      >
        <Globe size={16} className="text-cyan-400" />
        <span className="text-sm font-medium text-white">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
        <ChevronDown size={14} className={`text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-2 right-0 z-50 min-w-[150px] py-1 rounded-xl glass border border-white/20 shadow-xl">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setIsOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/10 ${
                  language === lang.code ? 'text-cyan-400' : 'text-white'
                }`}
              >
                <span>{lang.flag}</span>
                <span className="text-sm">{lang.name}</span>
                {language === lang.code && <Check size={14} className="ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA State
  const [step, setStep] = useState(1); // 1 = credentials, 2 = OTP verification
  const [sessionId, setSessionId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpHint, setOtpHint] = useState(''); // For testing
  const [resending, setResending] = useState(false);
  
  const { setUser, setToken } = useAuth();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('errors.required'));
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || t('errors.serverError'));
        setLoading(false);
        return;
      }
      
      if (data.requires_otp) {
        setSessionId(data.session_id);
        setOtpHint(data.otp_hint); // For testing
        setStep(2);
        toast.success(t('auth.verificationCode') + ' ✓');
        // Show OTP hint for testing
        toast.info(`OTP: ${data.otp_hint}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(t('errors.networkError'));
    }
    
    setLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    
    if (!otpCode || otpCode.length !== 6) {
      toast.error(t('auth.enterCode'));
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          otp: otpCode,
          session_id: sessionId 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || t('errors.serverError'));
        setLoading(false);
        return;
      }
      
      if (data.success) {
        // Track referral if there's a pending referral code (from any source)
        const pendingReferralData = safeLocalStorage.getItem('pending_referral_data');
        const referralFromCookie = getReferralCode();
        
        let referralCode = null;
        let fingerprint = null;
        
        // Priority 1: Check pending_referral_data (from registration flow)
        if (pendingReferralData) {
          try {
            const parsed = JSON.parse(pendingReferralData);
            referralCode = parsed.code;
            fingerprint = parsed.fingerprint;
          } catch (e) {
            console.error('Failed to parse pending referral data');
          }
        }
        
        // Priority 2: Check cookie/localStorage (from referral link click)
        if (!referralCode && referralFromCookie) {
          const referralData = getReferralData();
          referralCode = referralFromCookie;
          fingerprint = referralData.fingerprint;
        }
        
        // Track the referral if we found one
        if (referralCode && data.user?.id) {
          try {
            await fetch(`${BACKEND_URL}/api/referral/track`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                referred_user_id: data.user.id,
                referral_code: referralCode,
                device_fingerprint: fingerprint
              })
            });
            // Clear all referral tracking data after successful tracking
            safeLocalStorage.removeItem('pending_referral_data');
            clearReferralCode();
            console.log('[Referral] Successfully tracked referral:', referralCode);
          } catch (err) {
            console.error('Failed to track referral:', err);
          }
        }
        
        // Save user and token
        setUser(data.user);
        setToken(data.token);
        safeLocalStorage.setItem('auth_token', data.token);
        safeLocalStorage.setItem('user', JSON.stringify(data.user));
        
        toast.success(t('success.saved') + ' ✓');
        navigate('/');
      }
    } catch (error) {
      toast.error(t('errors.networkError'));
    }
    
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setResending(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-otp?session_id=${sessionId}&email=${email}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setOtpHint(data.otp_hint);
        toast.success(t('auth.resendCode') + ' ✓');
        toast.info(`OTP: ${data.otp_hint}`, { duration: 10000 });
      } else {
        toast.error(data.detail || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.networkError'));
    }
    
    setResending(false);
  };

  const handleBackToCredentials = () => {
    setStep(1);
    setOtpCode('');
    setSessionId('');
    setOtpHint('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="login-page">
      {/* Floating Language Toggle */}
      <FloatingLanguageToggle />
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-cyan-600 neon-glow">
            {step === 1 ? (
              <Bot size={32} className="text-black" />
            ) : (
              <ShieldCheck size={32} className="text-black" />
            )}
          </div>
          <h1 className="text-2xl font-bold">
            {step === 1 ? t('auth.login') : t('auth.verificationCode')}
          </h1>
          <p className="text-neutral-500 mt-2">
            {step === 1 ? t('auth.welcomeBack') : t('auth.enterCode')}
          </p>
        </div>

        {/* Step 1: Credentials Form */}
        {step === 1 && (
          <form onSubmit={handleCredentialsSubmit} className="glass rounded-2xl p-8 space-y-6">
            {/* Email */}
            <div>
              <label className="text-sm text-neutral-400 block mb-2">{t('auth.email')}</label>
              <div className="relative">
                <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
                <Input
                  data-testid="login-email"
                  type="email"
                  placeholder="example@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={isRTL ? 'pr-10' : 'pl-10'}
                  dir="ltr"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm text-neutral-400">{t('auth.password')}</label>
                <Link to="/forgot-password" className="text-xs text-cyan-400 hover:underline">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
                <Input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white`}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="remember" className="rounded" />
                <label htmlFor="remember" className="text-sm text-neutral-400">
                  {t('auth.rememberMe')}
                </label>
              </div>
            </div>

            {/* Submit */}
            <Button
              data-testid="login-submit"
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
            >
              {loading ? t('common.loading') : t('common.next')}
            </Button>

            {/* Register Link */}
            <p className="text-center text-sm text-neutral-500">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-cyan-400 hover:underline">
                {t('auth.signUp')}
              </Link>
            </p>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <form onSubmit={handleOTPSubmit} className="glass rounded-2xl p-8 space-y-6">
            {/* Info */}
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 text-center">
              <ShieldCheck className="mx-auto text-cyan-400 mb-2" size={32} />
              <p className="text-sm text-neutral-300">
                {t('auth.enterCode')}
              </p>
              <p className="font-mono text-cyan-400 mt-1" dir="ltr">{email}</p>
            </div>

            {/* OTP Input */}
            <div>
              <label className="text-sm text-neutral-400 block mb-2">{t('auth.verificationCode')}</label>
              <Input
                data-testid="otp-input"
                type="text"
                placeholder="000000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                dir="ltr"
                maxLength={6}
              />
            </div>

            {/* OTP Hint for Testing */}
            {otpHint && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
                <p className="text-xs text-amber-400">
                  🔑 OTP: <span className="font-mono font-bold">{otpHint}</span>
                </p>
              </div>
            )}

            {/* Submit */}
            <Button
              data-testid="verify-otp-submit"
              type="submit"
              className="w-full btn-primary"
              disabled={loading || otpCode.length !== 6}
            >
              {loading ? t('common.loading') : t('auth.verify')}
            </Button>

            {/* Resend OTP */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resending}
                className="text-sm text-cyan-400 hover:underline flex items-center justify-center gap-2 mx-auto"
              >
                <RefreshCw size={14} className={resending ? 'animate-spin' : ''} />
                {resending ? t('common.loading') : t('auth.resendCode')}
              </button>
            </div>

            {/* Back Button */}
            <button
              type="button"
              onClick={handleBackToCredentials}
              className="w-full text-center text-sm text-neutral-500 hover:text-neutral-400"
            >
              {isRTL ? '←' : '→'} {t('common.back')}
            </button>
          </form>
        )}

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-neutral-500 hover:text-neutral-400">
            {isRTL ? '←' : '→'} {t('common.back')}
          </Link>
        </div>
      </div>
    </div>
  );
}
